import { useState } from 'react';
import axios from 'axios';

const useInventoryData = ({
  inventoryUrl,
  salesTransactionsUrl,
  getAuthHeaders,
  pendingCheckout,
  showSuccessMessage,
  setError,
  accountId,
  archiveSoldItems = false,
}) => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingEditImages, setIsLoadingEditImages] = useState(false);
  const [lastQuery, setLastQuery] = useState({
    limit: 500,
    offset: 0,
    sortBy: 'dateCreated',
    sortOrder: 'desc',
    isSold: undefined,
    salesChannelId: '',
    salesLocation: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [bulkEntryMode, setBulkEntryMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    itemTitle: '',
    description: '',
    cost: '',
    listPrice: '',
    salesChannelId: '',
    salesLocation: '',
    itemLink: '',
    isSold: false,
    isMissing: false,
    itemQuantity: '',
  });
  const [editFormData, setEditFormData] = useState({
    itemTitle: '',
    description: '',
    cost: '',
    listPrice: '',
    salesChannelId: '',
    salesLocation: '',
    itemLink: '',
    isSold: false,
    isMissing: false,
    itemQuantity: '',
  });

  const fetchItems = async (params = {}) => {
    if (pendingCheckout) return;

    setIsLoadingItems(true);

    const config = await getAuthHeaders();
    const query = { ...lastQuery, ...params };
    setLastQuery(query);

    const buildSearchParams = (offset) => {
      const searchParams = new URLSearchParams();
      searchParams.set('limit', query.limit ?? 500);
      searchParams.set('offset', offset ?? query.offset ?? 0);
      if (query.sortBy) searchParams.set('sortBy', query.sortBy);
      if (query.sortOrder) searchParams.set('sortOrder', query.sortOrder);
      if (query.isSold !== undefined && query.isSold !== null) {
        searchParams.set('isSold', String(query.isSold));
      }
      if (query.isMissing !== undefined && query.isMissing !== null) {
        searchParams.set('isMissing', String(query.isMissing));
      }
      if (query.salesChannelId) searchParams.set('salesChannelId', query.salesChannelId);
      if (query.salesLocation) searchParams.set('salesLocation', query.salesLocation);
      return searchParams.toString();
    };

    try {
      const response = await axios.get(`${inventoryUrl}?${buildSearchParams()}`, config);
      const data = response.data.data || {};
      const itemsData = data.items || [];
      setItems(itemsData);
      setTotalCount(data.totalCount ?? data.count ?? itemsData.length);
    } catch (error) {
      console.error('Error fetching items:', error);
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        setError('Failed to fetch items');
      }
    } finally {
      setIsLoadingItems(false);
    }
  };

  const fetchItemCount = async (params = {}) => {
    if (pendingCheckout) return 0;

    const config = await getAuthHeaders();
    try {
      const searchParams = new URLSearchParams();
      if (params.isSold !== undefined && params.isSold !== null) {
        searchParams.set('isSold', String(params.isSold));
      }
      if (params.isMissing !== undefined && params.isMissing !== null) {
        searchParams.set('isMissing', String(params.isMissing));
      }
      const queryString = searchParams.toString();
      const url = queryString ? `${inventoryUrl}/count?${queryString}` : `${inventoryUrl}/count`;
      const response = await axios.get(url, config);
      const count = response.data?.data?.totalCount ?? 0;
      setTotalCount(count);
      return count;
    } catch (error) {
      console.error('Error fetching inventory count:', error);
      return 0;
    }
  };

  const searchItems = async (query, params = {}) => {
    if (!query.trim()) {
      fetchItems();
      return;
    }
    const config = await getAuthHeaders();
    setIsLoadingItems(true);

    const searchParams = new URLSearchParams();
    searchParams.set('q', query);
    if (params.limit != null) searchParams.set('limit', params.limit);
    if (params.offset != null) searchParams.set('offset', params.offset);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    axios.get(`${inventoryUrl}/search?${searchParams.toString()}`, config)
      .then((response) => {
        const responseData = response.data.data;
        // Backward compat: old format returns flat array, new format returns { items, totalCount, ... }
        const itemsData = Array.isArray(responseData) ? responseData : (responseData?.items || []);
        const count = Array.isArray(responseData)
          ? (response.data.count ?? itemsData.length)
          : (responseData?.totalCount ?? itemsData.length);
        setItems(itemsData);
        setTotalCount(count);
      })
      .catch((error) => {
        console.error('Error searching items:', error);
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          setError(error.response?.data?.error || 'Failed to search items');
        }
      })
      .finally(() => {
        setIsLoadingItems(false);
      });
  };

  const markAsSold = async (item) => {
    try {
      const config = await getAuthHeaders();

      const itemQty = item.itemQuantity ? parseInt(item.itemQuantity, 10) : 0;
      const qtySold = item.quantitySold ? parseInt(item.quantitySold, 10) : 1;

      if (itemQty === 0) {
        setError('Cannot mark item as sold: No quantity available');
        return;
      }

      const newQuantity = itemQty - qtySold;
      const shouldMarkSold = newQuantity === 0;

      const response = await axios.put(
        `${inventoryUrl}/${item.id}`,
        {
          isSold: shouldMarkSold,
          itemQuantity: newQuantity,
          accountId,
        },
        config
      );

      if (response.data.success) {
        const transactionData = {
          itemId: item.id,
          salesChannelId: item.salesChannelId,
          itemDescription: item.description,
          itemNumber: item.itemNumber || null,
          qtySold: qtySold,
          salePrice: parseFloat(item.listPrice),
          saleRecordedDate: new Date().toISOString(),
          accountId,
        };

        try {
          await axios.post(salesTransactionsUrl, transactionData, config);
        } catch (transactionError) {
          console.error('Error creating sales transaction:', transactionError);
        }

        const updatedItem = response.data.data;
        setItems(prevItems => prevItems.map(i => i.id === item.id ? updatedItem : i));
        showSuccessMessage(`Sold ${qtySold} item(s)!`);

        // Auto-archive if setting is enabled and item is fully sold
        // Brief delay to ensure backend has fully processed the inventory update
        // before the archive endpoint reads the item
        if (archiveSoldItems && shouldMarkSold && newQuantity === 0) {
          try {
            await new Promise(resolve => setTimeout(resolve, 500));
            await archiveItems([item.id]);
          } catch (archiveError) {
            console.error('Auto-archive failed (sale still succeeded):', archiveError);
          }
        }
      }
    } catch (error) {
      console.error('Error marking item as sold:', error);
      setError(error.response?.data?.error || 'Failed to mark item as sold');
    }
  };

  const markAsMissing = async (item) => {
    try {
      const config = await getAuthHeaders();
      const newMissing = !(item.isMissing ?? false);
      const response = await axios.put(
        `${inventoryUrl}/${item.id}`,
        { isMissing: newMissing, accountId },
        config
      );
      if (response.data.success) {
        const updatedItem = response.data.data;
        setItems(prevItems => prevItems.map(i => i.id === item.id ? updatedItem : i));
        showSuccessMessage(newMissing ? 'Item marked as missing' : 'Item marked as found');
      }
    } catch (error) {
      console.error('Error updating item missing status:', error);
      setError(error.response?.data?.error || 'Failed to update item');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      const config = await getAuthHeaders();
      const transactionsResponse = await axios.get(`${salesTransactionsUrl}/item/${itemId}`, config);
      const transactions = transactionsResponse.data?.data?.transactions || [];
      if (transactions.length > 0) {
        const deleteResults = await Promise.allSettled(
          transactions
            .map((transaction) => transaction?.id)
            .filter(Boolean)
            .map((transactionId) => axios.delete(`${salesTransactionsUrl}/${transactionId}`, config))
        );
        const failedDeletes = deleteResults.filter(result => result.status === 'rejected');
        if (failedDeletes.length > 0) {
          setError('Failed to delete one or more related sales transactions. Please try again.');
          return false;
        }
      }
      const response = await axios.delete(`${inventoryUrl}/${itemId}`, config);
      if (response.data.success) {
        setItems(prevItems => prevItems.filter(item => item.id !== itemId));
        showSuccessMessage('Item deleted successfully!');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting item:', error);
      setError(error.response?.data?.error || 'Failed to delete item');
      return false;
    }
  };

  const checkItemTransactions = async (itemId) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(`${salesTransactionsUrl}/item/${itemId}`, config);
      const transactions = response.data?.data?.transactions || [];
      const count = response.data?.data?.count ?? transactions.length;
      return { hasTransactions: count > 0, count };
    } catch (error) {
      console.error('Error checking item transactions:', error);
      return { hasTransactions: false, count: 0 };
    }
  };

  const startEditing = async (item) => {
    setEditingItem(item);
    setEditFormData({
      itemTitle: item.itemTitle,
      description: item.description,
      cost: item.cost.toString(),
      listPrice: item.listPrice.toString(),
      salesChannelId: item.salesChannelId,
      salesLocation: item.salesLocation,
      itemLink: item.itemLink || '',
      isSold: item.isSold ?? false,
      isMissing: item.isMissing ?? false,
      itemQuantity: item.itemQuantity?.toString() ?? '',
      images: item.images || [],
      mainImageUrl: item.mainImageUrl,
      itemNumber: item.itemNumber || null,
    });
    setShowForm(false);

    // Fetch full item detail for images (list responses may not include images array)
    if (!item.images || item.images.length === 0) {
      setIsLoadingEditImages(true);
      try {
        const config = await getAuthHeaders();
        const response = await axios.get(`${inventoryUrl}/${item.id}`, config);
        if (response.data.success) {
          const fullItem = response.data.data;
          setEditFormData(prev => ({
            ...prev,
            images: fullItem.images || [],
            mainImageUrl: fullItem.mainImageUrl || prev.mainImageUrl,
          }));
        }
      } catch (error) {
        console.error('Error fetching item details for edit:', error);
        setError('Failed to load item images. You can still edit other fields.');
      } finally {
        setIsLoadingEditImages(false);
      }
    }
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditFormData({ itemTitle: '', description: '', cost: '', listPrice: '', salesChannelId: '', salesLocation: '', itemLink: '', isSold: false, isMissing: false, itemQuantity: '' });
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const config = await getAuthHeaders();
      const { images: _images, mainImageUrl: _mainImageUrl, ...editFields } = editFormData;
      const submitData = { ...editFields, cost: parseFloat(editFields.cost), listPrice: parseFloat(editFields.listPrice), itemQuantity: editFields.itemQuantity ? parseInt(editFields.itemQuantity, 10) : 0 };

      const response = await axios.put(`${inventoryUrl}/${editingItem.id}`, { ...submitData, accountId }, config);
      if (response.data.success) {
        const updatedItem = response.data.data;
        setItems(prevItems => prevItems.map(item => item.id === editingItem.id ? updatedItem : item));
        cancelEditing();
        showSuccessMessage('Item updated successfully!');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      setError(error.response?.data?.error || 'Failed to update item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e, selectedImages = []) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const config = await getAuthHeaders();
      const submitData = { ...formData, cost: parseFloat(formData.cost), listPrice: parseFloat(formData.listPrice), itemQuantity: formData.itemQuantity ? parseInt(formData.itemQuantity, 10) : 0, accountId };
      const response = await axios.post(inventoryUrl, submitData, config);
      if (response.data.success) {
        const createdItem = response.data.data;

        if (selectedImages.length > 0) {
          await uploadItemImages(createdItem.id, selectedImages);
        }

        setFormData({ itemTitle: '', description: '', cost: '', listPrice: '', salesChannelId: '', salesLocation: '', itemLink: '', isSold: false, isMissing: false, itemQuantity: '' });
        if (!bulkEntryMode) setShowForm(false);
        await fetchItems();
        showSuccessMessage(bulkEntryMode ? 'Item added! Ready for next item.' : 'Item created successfully!');
      }
    } catch (error) {
      console.error('Error creating item:', error);
      setError(error.response?.data?.error || 'Failed to create item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickAddItem = async ({ itemTitle, cost, listPrice, salesChannelId, salesLocation }) => {
    setIsSubmitting(true);
    setError('');
    try {
      const config = await getAuthHeaders();
      const submitData = {
        itemTitle,
        description: itemTitle,
        cost: parseFloat(cost),
        listPrice: parseFloat(listPrice),
        salesChannelId,
        salesLocation: salesLocation || '',
        isSold: false,
        isMissing: false,
        itemQuantity: 1,
        accountId,
      };

      const response = await axios.post(inventoryUrl, submitData, config);
      if (response.data.success) {
        await fetchItems();
        showSuccessMessage('Item created successfully!');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error quick adding item:', error);
      setError(error.response?.data?.error || 'Failed to create item');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadItemImages = async (itemId, files) => {
    const uploadPromises = [];
    for (const file of files) {
      const uploadPromise = (async () => {
        try {
          const formData = new FormData();
          formData.append('image', file);
          const config = await getAuthHeaders();

          const response = await axios.post(
            `${inventoryUrl}/${itemId}/image`,
            formData,
            {
              headers: {
                ...config.headers,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          return response.data;
        } catch (error) {
          console.error(`Error uploading image ${file.name}:`, error.response?.data || error);
          throw error;
        }
      })();
      uploadPromises.push(uploadPromise);
    }
    await Promise.allSettled(uploadPromises);
  };

  const deleteItemImage = async (itemId, storagePath) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.delete(
        `${inventoryUrl}/${itemId}/image?storagePath=${encodeURIComponent(storagePath)}`,
        config
      );
      if (response.data.success) {
        const updatedItem = response.data.item;
        setEditFormData(prev => ({
          ...prev,
          images: updatedItem.images || []
        }));
        showSuccessMessage('Image deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setError(error.response?.data?.error || 'Failed to delete image');
    }
  };

  const setMainItemImage = async (itemId, imageUrl) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.put(
        `${inventoryUrl}/${itemId}/image/main`,
        { imageUrl, accountId },
        config
      );
      if (response.data.success) {
        const updatedItem = response.data.item;
        setEditFormData(prev => ({
          ...prev,
          images: updatedItem.images || [],
          mainImageUrl: updatedItem.mainImageUrl
        }));
        showSuccessMessage('Main image updated successfully!');
      }
    } catch (error) {
      console.error('Error setting main image:', error);
      setError(error.response?.data?.error || 'Failed to set main image');
    }
  };

  const archiveItems = async (itemIds) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${inventoryUrl}/archive`,
        { itemIds },
        config
      );
      if (response.data.success) {
        setItems(prevItems => prevItems.filter(item => !itemIds.includes(item.id)));
        showSuccessMessage(`${response.data.data.archivedCount} item(s) archived successfully`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error archiving items:', error);
      const errorMsg = error.response?.status === 429
        ? error.response?.data?.error || 'Monthly archive limit exceeded'
        : error.response?.data?.error || 'Failed to archive items';
      setError(errorMsg);
      return false;
    }
  };

  const handleEditImageUpload = async (files) => {
    if (!editingItem) return;
    try {
      await uploadItemImages(editingItem.id, files);
      const config = await getAuthHeaders();
      const response = await axios.get(`${inventoryUrl}/${editingItem.id}`, config);
      if (response.data.success) {
        const updatedItem = response.data.data;
        setEditFormData(prev => ({
          ...prev,
          images: updatedItem.images || [],
          mainImageUrl: updatedItem.mainImageUrl
        }));
        showSuccessMessage('Images uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error.response?.data?.error || 'Failed to upload images');
    }
  };

  return {
    items,
    setItems,
    totalCount,
    isLoadingItems,
    isSubmitting,
    fetchItemCount,
    showForm,
    setShowForm,
    bulkEntryMode,
    setBulkEntryMode,
    editingItem,
    setEditingItem,
    formData,
    setFormData,
    editFormData,
    setEditFormData,
    fetchItems,
    searchItems,
    markAsSold,
    markAsMissing,
    deleteItem,
    checkItemTransactions,
    startEditing,
    cancelEditing,
    handleEditInputChange,
    handleEditSubmit,
    handleInputChange,
    handleSubmit,
    quickAddItem,
    uploadItemImages,
    handleEditImageUpload,
    deleteItemImage,
    setMainItemImage,
    archiveItems,
    isLoadingEditImages,
  };
};

export default useInventoryData;
